import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Campaign, archiveCampaign, unarchiveCampaign, approvePreview } from '../api/campaigns';
import { getPermissions } from '../api/users';
import { ActivityFeed } from './ActivityFeed';
import { CommentThread } from './CommentThread';

/** Normalize preview_link to a string array (handles legacy string values). */
function normalizePreviewLinks(value: string[] | string | null | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  // Legacy string — wrap in array
  return [value];
}

interface StatusProgressionModalProps {
  campaign: Campaign;
  isOpen: boolean;
  onClose: () => void;
}

// Valid SLA status keys
type SlaStatusKey = 'on_track' | 'warning' | 'breached' | 'not_applicable';

const VALID_SLA_STATUS_KEYS: readonly SlaStatusKey[] = [
  'on_track',
  'warning',
  'breached',
  'not_applicable',
] as const;

function isValidSlaStatus(status: string | null | undefined): status is SlaStatusKey {
  return (
    status !== null &&
    status !== undefined &&
    VALID_SLA_STATUS_KEYS.includes(status as SlaStatusKey)
  );
}

function getSlaStatusConfig(status: string | null | undefined) {
  return SLA_STATUS_CONFIG[isValidSlaStatus(status) ? status : 'not_applicable'];
}

// Status progression steps in order
const STATUS_STEPS = [
  {
    key: 'waiting_for_client_brief',
    label: 'Waiting for Client Brief',
    shortLabel: 'Brief',
    phase: 'creative',
    description: 'Awaiting FordShare Activity ID',
  },
  {
    key: 'asset_in_development',
    label: 'Asset in Development',
    shortLabel: 'Development',
    phase: 'creative',
    description: 'FordShare Activity ID has been set',
  },
  {
    key: 'ad_copy_creation',
    label: 'Ad Copy Creation',
    shortLabel: 'Ad Copy',
    phase: 'creative',
    description: 'FordShare Activity ID set and at least one asset saved',
  },
  {
    key: 'activation_in_setup',
    label: 'Activation in Setup',
    shortLabel: 'Setup',
    phase: 'activation',
    description: 'Asset approved, activation being configured',
  },
  {
    key: 'waiting_for_preview_approval',
    label: 'Waiting for Preview Approval',
    shortLabel: 'Preview',
    phase: 'activation',
    description: 'Preview link added, awaiting Local Ford approval',
  },
  {
    key: 'preview_approved',
    label: 'Preview Approved',
    shortLabel: 'Approved',
    phase: 'activation',
    description: 'Preview approved, ready for launch',
  },
  {
    key: 'campaign_live',
    label: 'Campaign Live',
    shortLabel: 'Live',
    phase: 'complete',
    description: 'Campaign is live and running',
  },
];

/** Search RSA campaigns (channel=Paid Search, format=Search) skip early statuses. */
const SEARCH_RSA_SKIPPED_STATUSES = new Set(['waiting_for_client_brief', 'asset_in_development']);

function isSearchRsa(campaign: Campaign): boolean {
  return campaign.channel === 'Paid Search' && campaign.format?.toLowerCase().trim() === 'search';
}

/** Search RSA description overrides — these steps have different meanings without FordShare. */
const SEARCH_RSA_DESCRIPTIONS: Record<string, string> = {
  ad_copy_creation: 'Ready for ad copy creation',
};

function getStatusSteps(campaign: Campaign) {
  if (isSearchRsa(campaign)) {
    return STATUS_STEPS.filter((step) => !SEARCH_RSA_SKIPPED_STATUSES.has(step.key)).map((step) =>
      step.key in SEARCH_RSA_DESCRIPTIONS
        ? { ...step, description: SEARCH_RSA_DESCRIPTIONS[step.key] }
        : step,
    );
  }
  return STATUS_STEPS;
}

// SLA status colors and labels
const SLA_STATUS_CONFIG = {
  on_track: {
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    borderColor: 'border-green-300',
    icon: '✓',
    label: 'On Track',
  },
  warning: {
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    borderColor: 'border-yellow-300',
    icon: '⚠',
    label: 'Approaching Deadline',
  },
  breached: {
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    borderColor: 'border-red-300',
    icon: '✗',
    label: 'SLA Breached',
  },
  not_applicable: {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-600',
    borderColor: 'border-gray-300',
    icon: '—',
    label: 'No SLA',
  },
};

// Phase labels
const PHASE_CONFIG = {
  creative: {
    label: 'Creative Localisation',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  activation: {
    label: 'Media Activation',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  complete: {
    label: 'Complete',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Not set';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function StatusProgressionModal({ campaign, isOpen, onClose }: StatusProgressionModalProps) {
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const queryClient = useQueryClient();

  // Fetch user permissions to check if user can archive campaigns
  const { data: permissions } = useQuery({
    queryKey: ['user-permissions'],
    queryFn: getPermissions,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Check if user has permission to archive campaigns
  const canArchive = permissions?.can_archive ?? false;
  const canApprovePreview = permissions?.can_approve_preview_link ?? false;

  // Mutation to approve preview
  const approvePreviewMutation = useMutation({
    mutationFn: () => approvePreview(campaign.id),
    onSuccess: () => {
      toast.success('Preview approved successfully!');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-events', campaign.id] });
    },
    onError: (error: unknown) => {
      const axiosDetail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      const message =
        axiosDetail ?? (error instanceof Error ? error.message : 'Failed to approve preview');
      toast.error(message);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveCampaign(campaign.id, archiveReason || undefined),
    onSuccess: () => {
      toast.success('Campaign archived successfully');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-events', campaign.id] });
      setShowArchiveConfirm(false);
      setArchiveReason('');
      onClose();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to archive campaign';
      toast.error(message);
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => unarchiveCampaign(campaign.id),
    onSuccess: () => {
      toast.success('Campaign unarchived successfully');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-events', campaign.id] });
      onClose();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to unarchive campaign';
      toast.error(message);
    },
  });

  if (!isOpen) return null;

  const steps = getStatusSteps(campaign);
  // If status maps to a skipped step (e.g., legacy Search RSA data), default to first step
  const rawIndex = steps.findIndex((step) => step.key === campaign.status);
  const currentStatusIndex = rawIndex >= 0 ? rawIndex : 0;
  // Look up by key — Search RSA campaigns skip early steps, so the index varies.
  const previewStepIndex = steps.findIndex((step) => step.key === 'waiting_for_preview_approval');
  const slaConfig = getSlaStatusConfig(campaign.sla_status);
  const currentPhase = campaign.sla_phase || 'planning';

  // Format the SLA deadline date (backend-computed, weekday-aware).
  // We use the exact deadline date from the backend rather than reconstructing
  // it from sla_days_remaining, because days_remaining is a working-day count
  // and cannot be mapped back to a calendar date on the frontend.
  const getDeadlineInfo = () => {
    if (!campaign.sla_deadline) {
      return { deadline: null, label: '' };
    }

    // sla_deadline is a date-only ISO string (YYYY-MM-DD), which JS parses as
    // UTC midnight. Format in UTC so negative-offset timezones don't display
    // the previous day.
    const deadline = new Date(campaign.sla_deadline);
    const formattedDeadline = deadline.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });

    if (currentPhase === 'creative_localisation') {
      return {
        deadline: formattedDeadline,
        label: 'Creative Localisation Deadline',
      };
    } else if (currentPhase === 'media_activation') {
      return {
        deadline: formattedDeadline,
        label: 'Campaign Launch Date',
      };
    }

    return { deadline: null, label: '' };
  };

  const deadlineInfo = getDeadlineInfo();

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} aria-hidden="true" />

      {/* Modal */}
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="status-modal-title"
      >
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 id="status-modal-title" className="text-lg font-semibold text-gray-900">
                Campaign Status
              </h2>
              <p
                className="text-sm text-gray-500 mt-0.5 truncate max-w-md"
                title={campaign.campaign_id}
              >
                {campaign.campaign_id}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* SLA Status Banner */}
            <div
              className={`rounded-lg p-4 mb-6 border ${slaConfig.bgColor} ${slaConfig.borderColor}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`text-2xl ${slaConfig.textColor}`}>{slaConfig.icon}</span>
                  <div>
                    <h3 className={`font-semibold ${slaConfig.textColor}`}>{slaConfig.label}</h3>
                    {campaign.sla_message && (
                      <p className={`text-sm ${slaConfig.textColor} opacity-80`}>
                        {campaign.sla_message}
                      </p>
                    )}
                  </div>
                </div>
                {campaign.sla_days_remaining !== null &&
                  campaign.sla_status !== 'not_applicable' && (
                    <div className={`text-right ${slaConfig.textColor}`}>
                      <div className="text-2xl font-bold">
                        {campaign.sla_days_remaining < 0
                          ? Math.abs(campaign.sla_days_remaining)
                          : campaign.sla_days_remaining}
                      </div>
                      <div className="text-xs uppercase tracking-wide opacity-80">
                        {campaign.sla_days_remaining < 0
                          ? 'working days overdue'
                          : 'working days left'}
                      </div>
                    </div>
                  )}
              </div>

              {/* Deadline info */}
              {deadlineInfo.deadline && (
                <div
                  className={`mt-3 pt-3 border-t ${slaConfig.borderColor} flex justify-between text-sm`}
                >
                  <span className={`${slaConfig.textColor} opacity-80`}>{deadlineInfo.label}:</span>
                  <span className={`font-medium ${slaConfig.textColor}`}>
                    {deadlineInfo.deadline}
                  </span>
                </div>
              )}
            </div>

            {/* Activity Feed */}
            <div className="mb-6 -mx-6">
              <ActivityFeed campaignId={campaign.id} />
            </div>

            {/* Campaign Dates */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                  Campaign Start
                </div>
                <div className="font-medium text-gray-900">{formatDate(campaign.start_date)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                  Campaign End
                </div>
                <div className="font-medium text-gray-900">{formatDate(campaign.end_date)}</div>
              </div>
            </div>

            {/* Phase Indicator */}
            <div className="mb-4">
              <div className="flex gap-2">
                <div
                  className={`px-3 py-1 rounded-full text-xs font-medium ${PHASE_CONFIG.creative.bgColor} ${PHASE_CONFIG.creative.color} ${currentPhase === 'creative_localisation' ? 'ring-2 ring-blue-400' : 'opacity-60'}`}
                >
                  Creative Localisation
                </div>
                <div
                  className={`px-3 py-1 rounded-full text-xs font-medium ${PHASE_CONFIG.activation.bgColor} ${PHASE_CONFIG.activation.color} ${currentPhase === 'media_activation' ? 'ring-2 ring-purple-400' : 'opacity-60'}`}
                >
                  Media Activation
                </div>
                <div
                  className={`px-3 py-1 rounded-full text-xs font-medium ${PHASE_CONFIG.complete.bgColor} ${PHASE_CONFIG.complete.color} ${currentPhase === 'completed' ? 'ring-2 ring-green-400' : 'opacity-60'}`}
                >
                  Complete
                </div>
              </div>
            </div>

            {/* Status Progression Timeline */}
            <div className="relative">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Status Progression</h4>

              <div className="space-y-0">
                {steps.map((step, index) => {
                  const isCompleted = index < currentStatusIndex;
                  const isCurrent = index === currentStatusIndex;
                  const isPending = index > currentStatusIndex;

                  return (
                    <div key={step.key} className="flex items-start relative">
                      {/* Connector line */}
                      {index < steps.length - 1 && (
                        <div
                          className={`absolute left-3 top-6 w-0.5 h-full ${
                            isCompleted ? 'bg-green-400' : 'bg-gray-200'
                          }`}
                        />
                      )}

                      {/* Step indicator */}
                      <div
                        className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isCompleted
                            ? 'bg-green-500 text-white'
                            : isCurrent
                              ? 'bg-blue-500 text-white ring-4 ring-blue-100'
                              : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        {isCompleted ? (
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <span className="text-xs font-medium">{index + 1}</span>
                        )}
                      </div>

                      {/* Step content */}
                      <div className={`ml-4 pb-6 ${isPending ? 'opacity-50' : ''}`}>
                        <div className="flex items-center gap-2">
                          <h5
                            className={`font-medium ${isCurrent ? 'text-blue-600' : isCompleted ? 'text-gray-900' : 'text-gray-500'}`}
                          >
                            {step.label}
                          </h5>
                          {isCurrent && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {step.key === 'preview_approved' && campaign.preview_approved_by ? (
                            <>
                              Approved by {campaign.preview_approved_by.name}
                              {campaign.preview_approved_at && (
                                <>
                                  {' '}
                                  at{' '}
                                  {new Date(campaign.preview_approved_at).toLocaleTimeString(
                                    'en-GB',
                                    { hour: '2-digit', minute: '2-digit' },
                                  )}{' '}
                                  on{' '}
                                  {new Date(campaign.preview_approved_at).toLocaleDateString(
                                    'en-GB',
                                    { day: 'numeric', month: 'numeric', year: '2-digit' },
                                  )}
                                </>
                              )}
                              . Ready for launch
                            </>
                          ) : (
                            step.description
                          )}
                        </p>

                        {/* Preview approval action box - inline with stage 4 */}
                        {step.key === 'waiting_for_preview_approval' &&
                          isCurrent &&
                          (() => {
                            const previewLinks = normalizePreviewLinks(campaign.preview_link);
                            return (
                              previewLinks.length > 0 &&
                              canApprovePreview && (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                  <p className="text-xs text-gray-600 mb-2">
                                    You are authorized to approve this preview. Please review the
                                    link{previewLinks.length > 1 ? 's' : ''} and click Approve, or
                                    leave feedback in Preview Comments below.
                                  </p>
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                      {previewLinks.length === 1 ? (
                                        <a
                                          href={previewLinks[0]}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
                                        >
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                            />
                                          </svg>
                                          View Preview
                                        </a>
                                      ) : (
                                        previewLinks.map((link, index) => (
                                          <a
                                            key={index}
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
                                          >
                                            <svg
                                              className="w-4 h-4"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                              />
                                            </svg>
                                            View Preview {index + 1}
                                          </a>
                                        ))
                                      )}
                                    </div>
                                    <button
                                      onClick={() => approvePreviewMutation.mutate()}
                                      disabled={approvePreviewMutation.isPending}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                                    >
                                      {approvePreviewMutation.isPending ? (
                                        'Approving...'
                                      ) : (
                                        <>
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M5 13l4 4L19 7"
                                            />
                                          </svg>
                                          Approve
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              )
                            );
                          })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Comments Section */}
            <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
              <CommentThread campaignId={campaign.id} stage="general" />
              {/* Preview Feedback only shows from "Waiting for Preview Approval" status onwards */}
              {currentStatusIndex >= previewStepIndex && (
                <CommentThread campaignId={campaign.id} stage="preview" />
              )}
            </div>
          </div>

          {/* Archive Confirmation */}
          {canArchive && showArchiveConfirm && (
            <div className="border-t border-gray-200 px-6 py-4 bg-slate-50">
              <div className="mb-3">
                <label
                  htmlFor="archive-reason"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reason for archiving (optional)
                </label>
                <textarea
                  id="archive-reason"
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  placeholder="e.g., Duplicate campaign, Client cancelled, Test data..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                  maxLength={1000}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => archiveMutation.mutate()}
                  disabled={archiveMutation.isPending}
                  className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium disabled:opacity-50"
                >
                  {archiveMutation.isPending ? 'Archiving...' : 'Confirm Archive'}
                </button>
                <button
                  onClick={() => {
                    setShowArchiveConfirm(false);
                    setArchiveReason('');
                  }}
                  disabled={archiveMutation.isPending}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex gap-2">
              {canArchive &&
                (campaign.is_archived ? (
                  <button
                    onClick={() => unarchiveMutation.mutate()}
                    disabled={unarchiveMutation.isPending}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {unarchiveMutation.isPending ? 'Unarchiving...' : 'Unarchive Campaign'}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowArchiveConfirm(true)}
                    disabled={showArchiveConfirm}
                    className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors font-medium disabled:opacity-50"
                  >
                    Archive Campaign
                  </button>
                ))}
              <button
                onClick={onClose}
                className={`${canArchive ? 'flex-1' : 'w-full'} px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
